import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  BookOpen,
  Download,
  Loader2,
  LogIn,
  MonitorPlay,
  RefreshCw,
} from "lucide-react";
import { getTeacherAccountSoftwareUsage } from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import { showErrorToast } from "../../../components/ConfirmDialog";
import Pagination from "../../../components/common/Pagination";

const fmtDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("vi-VN");
};

const shortUserAgent = (ua) => {
  if (!ua) return "—";
  const s = String(ua);
  if (s.length <= 72) return s;
  return `${s.slice(0, 69)}…`;
};

const docTypeLabel = (type) => {
  const map = {
    baigiang: "Bài giảng",
    giaotrinh: "Giáo trình",
    baitap: "Bài tập",
    video: "Video",
    dethi: "Đề thi",
    khac: "Khác",
  };
  return map[String(type || "").toLowerCase()] || type || "—";
};

const actionLabel = (action, docType) => {
  const a = String(action || "").toLowerCase();
  const t = String(docType || "").toLowerCase();
  if (a === "download") return "Tải xuống";
  if (a === "view" && (t === "baigiang" || t === "presentation")) {
    return "Trình chiếu / xem bài giảng";
  }
  if (a === "view") return "Xem tài liệu";
  return action || "—";
};

const loginStatusLabel = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "success") return { text: "Thành công", cls: "text-green-700 bg-green-50 border-green-200" };
  if (s === "failed") return { text: "Thất bại", cls: "text-red-700 bg-red-50 border-red-200" };
  return { text: status || "—", cls: "text-gray-600 bg-gray-50 border-gray-200" };
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
      <Icon className="h-4 w-4 text-red-500" />
      {label}
    </div>
    <p className="text-2xl font-black text-gray-900">{value}</p>
    {sub ? <p className="mt-1 text-xs text-gray-500">{sub}</p> : null}
  </div>
);

const TeacherAccountUsagePanel = ({ employeeId }) => {
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [logTab, setLogTab] = useState("login");
  const [loginLogs, setLoginLogs] = useState([]);
  const [documentLogs, setDocumentLogs] = useState([]);
  const [loginTotal, setLoginTotal] = useState(0);
  const [documentTotal, setDocumentTotal] = useState(0);
  const [loginPage, setLoginPage] = useState(1);
  const [documentPage, setDocumentPage] = useState(1);
  const [pageSize] = useState(10);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadSummary = useCallback(async () => {
    if (!employeeId) return;
    setLoadingSummary(true);
    try {
      const data = await getTeacherAccountSoftwareUsage(employeeId, { part: "summary" });
      setSummary(data);
    } catch (e) {
      setSummary(null);
      showErrorToast(e?.response?.data?.error || "Không tải được thống kê sử dụng.");
    } finally {
      setLoadingSummary(false);
    }
  }, [employeeId]);

  const loadLogs = useCallback(async () => {
    if (!employeeId) return;
    setLoadingLogs(true);
    try {
      if (logTab === "login") {
        const data = await getTeacherAccountSoftwareUsage(employeeId, {
          part: "login_logs",
          page: loginPage,
          limit: pageSize,
        });
        setLoginLogs(Array.isArray(data?.items) ? data.items : []);
        setLoginTotal(Number(data?.total || 0));
      } else {
        const data = await getTeacherAccountSoftwareUsage(employeeId, {
          part: "document_logs",
          page: documentPage,
          limit: pageSize,
        });
        setDocumentLogs(Array.isArray(data?.items) ? data.items : []);
        setDocumentTotal(Number(data?.total || 0));
      }
    } catch (e) {
      if (logTab === "login") {
        setLoginLogs([]);
        setLoginTotal(0);
      } else {
        setDocumentLogs([]);
        setDocumentTotal(0);
      }
      showErrorToast(e?.response?.data?.error || "Không tải được nhật ký.");
    } finally {
      setLoadingLogs(false);
    }
  }, [employeeId, logTab, loginPage, documentPage, pageSize]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    setLoginPage(1);
    setDocumentPage(1);
  }, [employeeId]);

  if (!employeeId) return null;

  const quota = summary?.document_quota;
  const courses = summary?.assigned_courses || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-gray-700">
          <Activity className="h-4 w-4 text-red-600" />
          Thống kê sử dụng phần mềm
        </div>
        <button
          type="button"
          onClick={() => {
            loadSummary();
            loadLogs();
          }}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loadingSummary || loadingLogs ? "animate-spin" : ""}`} />
          Làm mới
        </button>
      </div>

      {loadingSummary && !summary ? (
        <div className="flex items-center justify-center py-10 text-gray-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Đang tải thống kê...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard
              icon={LogIn}
              label="Đăng nhập (30 ngày)"
              value={summary?.login?.success_30d ?? 0}
              sub={
                summary?.login?.last?.login_time
                  ? `Lần cuối: ${fmtDateTime(summary.login.last.login_time)}`
                  : "Chưa có lần đăng nhập"
              }
            />
            <StatCard
              icon={MonitorPlay}
              label="Trình chiếu (30 ngày)"
              value={summary?.documents?.presentations_30d ?? 0}
              sub={`Tổng: ${summary?.documents?.presentation_count ?? 0} lượt`}
            />
            <StatCard
              icon={Download}
              label="Tải tài liệu (30 ngày)"
              value={summary?.documents?.downloads_30d ?? 0}
              sub={`Xem tài liệu: ${summary?.documents?.view_count ?? 0} lượt`}
            />
            <StatCard
              icon={BookOpen}
              label="Khóa học được gán"
              value={courses.length}
              sub={
                quota?.doc_quota_exempt
                  ? "Hạn mức tài liệu: miễn trừ"
                  : `Đã dùng tháng này: ${quota?.used_this_month ?? 0}/${(quota?.monthly_limit ?? 5) + (quota?.bonus_balance ?? 0)}`
              }
            />
          </div>

          {courses.length > 0 ? (
            <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                Khóa học được phân quyền
              </p>
              <div className="flex flex-wrap gap-2">
                {courses.map((c) => (
                  <span
                    key={c.id}
                    className="inline-flex rounded-full border border-red-100 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700"
                  >
                    {c.name}
                    {c.code ? ` (${c.code})` : ""}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="flex border-b border-gray-100">
          <button
            type="button"
            onClick={() => setLogTab("login")}
            className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wide transition ${
              logTab === "login"
                ? "border-b-2 border-red-600 bg-red-50/50 text-red-700"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            Nhật ký đăng nhập
          </button>
          <button
            type="button"
            onClick={() => setLogTab("document")}
            className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wide transition ${
              logTab === "document"
                ? "border-b-2 border-red-600 bg-red-50/50 text-red-700"
                : "text-gray-500 hover:bg-gray-50"
            }`}
          >
            Nhật ký tài liệu / bài giảng
          </button>
        </div>

        <div className="overflow-auto">
          {logTab === "login" ? (
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-gray-50/90 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Thời gian</th>
                  <th className="px-4 py-3">Trạng thái</th>
                  <th className="px-4 py-3">IP</th>
                  <th className="px-4 py-3">Thiết bị / trình duyệt</th>
                </tr>
              </thead>
              <tbody>
                {loginLogs.map((log) => {
                  const st = loginStatusLabel(log.status);
                  return (
                    <tr key={log.id} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-800">{fmtDateTime(log.login_time)}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${st.cls}`}
                        >
                          {st.text}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{log.ip_address || "—"}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {shortUserAgent(log.user_agent)}
                      </td>
                    </tr>
                  );
                })}
                {!loadingLogs && loginLogs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      Chưa có nhật ký đăng nhập.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : (
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-gray-50/90 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Thời gian</th>
                  <th className="px-4 py-3">Hoạt động</th>
                  <th className="px-4 py-3">Tài liệu</th>
                  <th className="px-4 py-3">Khóa học</th>
                </tr>
              </thead>
              <tbody>
                {documentLogs.map((log) => (
                  <tr key={log.id} className="border-t border-gray-100">
                    <td className="px-4 py-3 text-gray-800">{fmtDateTime(log.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-800">
                        {actionLabel(log.action, log.document_type)}
                      </p>
                      <p className="text-xs text-gray-500">{docTypeLabel(log.document_type)}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {log.document_title || log.document_file_name || "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {log.course_name || log.course_code || "—"}
                    </td>
                  </tr>
                ))}
                {!loadingLogs && documentLogs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      Chưa có nhật ký tải / xem / trình chiếu tài liệu.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </div>

        {loadingLogs ? (
          <div className="flex items-center justify-center border-t border-gray-100 py-4 text-sm text-gray-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Đang tải nhật ký...
          </div>
        ) : null}

        <div className="border-t border-gray-100 px-2 py-2">
          <Pagination
            totalRecords={logTab === "login" ? loginTotal : documentTotal}
            currentPage={logTab === "login" ? loginPage : documentPage}
            pageSize={pageSize}
            onPageChange={logTab === "login" ? setLoginPage : setDocumentPage}
            onPageSizeChange={() => {}}
          />
        </div>
      </div>
    </div>
  );
};

export default TeacherAccountUsagePanel;
